import React, { useEffect } from "react";
import { flexRender } from "@tanstack/react-table";
import PopupComponent from "@components/PopupComponent";
import { filter, CopyToCliboard } from "./helpers";
import {
  FormatWithCommas,
  FormatNegativeWithParentheses,
} from "@helpers/helpers";
import clsx from "clsx";
import {
  MAX_CHARACTER_TRUNCATE,
  FIRST_INDEX,
  SECOND_INDEX,
} from "./renderCell.constants";
import { Filter, Info, LogOut } from "lucide-react";
import { useTheme } from "@stores/ThemeContext";

const renderCell = (
  row,
  cell,
  popupOpen,
  setPopupOpen,
  appliedFilters,
  saveAndApplyFilter,
  translate,
  isCellMenuOpen = false,
  rowId = null,
  isDarkMode = false
) => {
  const { column } = cell;
  const columnDef = column.columnDef;
  const context = cell.getContext();
  const value = context.getValue();
  const passedFilterCheck =
    !columnDef.filterCheck ||
    (columnDef.filterCheck && columnDef.filterCheck(value));
  const truncate = columnDef.truncate;
  const cellHasPopupOpen = popupOpen === context.cell.id;
  const shouldShowTruncatePopup =
    // truncate && !!value && value.length >= parseInt(columnDef.size / 7);
    truncate &&
    !!value &&
    value.length >= parseInt(columnDef.size / MAX_CHARACTER_TRUNCATE);

  const passedActionCheck =
    columnDef.cellAction &&
    (!columnDef.cellActionCheck ||
      (columnDef.cellActionCheck && columnDef.cellActionCheck(value, context)));

  return cell.getIsGrouped() ? (
    // If it's a grouped cell, add an expander and row count
    <>
      <button
        {...{
          onClick: row.getToggleExpandedHandler(),
          style: {
            cursor: row.getCanExpand() ? "pointer" : "normal",
          },
        }}
      >
        {row.getIsExpanded() ? "👇" : "👉"}{" "}
        {flexRender(columnDef.cell, context)} {"("}
        {row.subRows.length}
        {")"}
      </button>
    </>
  ) : cell.getIsPlaceholder() ? null : (columnDef.canFilter ||
      shouldShowTruncatePopup) && // Otherwise, just render the regular cell // For cells with repeated values, render null
    passedFilterCheck ? (
    <>
      <div
        className={clsx(
          "w-full cursor-pointer text-tableTextColor hover:text-tableTextColorHover flex relative dark:text-white",
          columnDef.alignment
            ? `justify-${columnDef.alignment} items-${columnDef.alignment} text-${columnDef.alignment}`
            : "justify-center items-center text-center"
          // 'font-semibold '
        )}
      >
        <div
          data-testid={`cell-actions-${row.id}-${column.id}`}
          className={clsx(
            `absolute z-[2] w-full h-full left-0 top-0 p-0 bg-inherit transition-visibility  flex-row`,
            isCellMenuOpen ===
              `cell-${rowId}-${cell.column.columnDef.accessorKey}`
              ? "flex bg-opacity-80 bg-inherit items-center justify-center gap-3"
              : "hidden"
          )}
        >
          {columnDef.cellAction &&
            (!columnDef.cellActionCheck ||
              (columnDef.cellActionCheck &&
                columnDef.cellActionCheck(value, context))) && (
              <Info
                size={18}
                data-tooltip-target="tooltip-default"
                onClick={() => {
                  columnDef.cellAction(value, context);
                }}
                title={columnDef.cellActionHelper}
                className="mx-1 hover:scale-110 hover:text-tableTextColorHover transition-all duration-200"
              />
            )}
          {columnDef.displayInPopup && (
            <>
              {popupOpen === context.cell.id && (
                <PopupComponent
                  open={true}
                  cellData={context}
                  value={value}
                  setPopupOpen={setPopupOpen}
                />
              )}
              <Info
                size={18}
                className="mx-1 hover:scale-110 hover:text-tableTextColorHover transition-all duration-200"
                onClick={() => {
                  setPopupOpen(context.cell.id);
                }}
              />
            </>
          )}

          {columnDef.canFilter && value && (
            <div title="Filter by this value">
              <Filter
                data-testid={`cell-filter-${row.id}-${column.id}`}
                onClick={() => {
                  filter(
                    columnDef,
                    value,
                    row.original,
                    appliedFilters,
                    saveAndApplyFilter,
                    translate,
                    isDarkMode
                  );
                }}
                className="size-6 dark:text-white hover:scale-110 hover:text-tableTextColorHover transition-all duration-200"
              />
            </div>
          )}
          {!columnDef.hideCopy && value && (
            <CopyToCliboard value={value} isDarkMode={isDarkMode} />
          )}
          {shouldShowTruncatePopup && (
            <div title="Show full text">
              <Info
                size={18}
                className="mx-1 hover:scale-110 hover:text-tableTextColorHover transition-all duration-200"
                onClick={() => setPopupOpen(context.cell.id)}
                data-testid={`cell-popup-${row.id}-${column.id}`}
              />
            </div>
          )}
        </div>
        <div
          className={clsx(
            columnDef.extraClass,
            truncate && "truncate overflow-visible justify-start",
            ((passedFilterCheck && columnDef.canFilter) ||
              shouldShowTruncatePopup) &&
              "group-hover:opacity-20",
            isCellMenuOpen ===
              `cell-${rowId}-${cell.column.columnDef.accessorKey}` &&
              "opacity-20",
            columnDef.capitalize && "lowercase first-letter:uppercase"
          )}
        >
          {columnDef.splitCellValue ? (
            <span className="text-sm">
              {value?.split(columnDef.splitCellValue)[FIRST_INDEX]}
              <br />
              {value?.split(columnDef.splitCellValue)[SECOND_INDEX]}
            </span>
          ) : columnDef.showThousandSeperator && !columnDef.cell ? (
            <span>{FormatNegativeWithParentheses(value)}</span>
          ) : (
            flexRender(columnDef.cell, context)
          )}
        </div>
      </div>
      {cellHasPopupOpen && shouldShowTruncatePopup && (
        <PopupComponent
          open={popupOpen}
          rawValue={
            columnDef.splitCellValue ? (
              <span className="text-sm">
                <b className="text-md">
                  {value?.split(columnDef.splitCellValue)[FIRST_INDEX]}
                </b>
                <br />
                {value?.split(columnDef.splitCellValue)[SECOND_INDEX]}
              </span>
            ) : (
              value
            )
          }
          setPopupOpen={setPopupOpen}
          addBlur
        />
      )}
    </>
  ) : passedFilterCheck && columnDef.displayInPopup ? (
    <PopupComponent
      open={cellHasPopupOpen}
      cellData={context}
      value={value}
      setPopupOpen={setPopupOpen}
    />
  ) : (
    <div
      className={clsx(truncate && "truncate overflow-visible justify-start")}
    >
      <div
        onClick={() => {
          passedActionCheck && columnDef.cellAction(value, context);
        }}
        className={clsx(
          `font-sans flex group text-tableTextColor dark:text-white`,
          columnDef.alignment
            ? `justify-${columnDef.alignment} items-${columnDef.alignment} text-${columnDef.alignment}`
            : "justify-center items-center text-center",
          // 'font-semibold',
          passedActionCheck && "cursor-pointer hover:text-tableTextColorHover",
          passedFilterCheck && columnDef.canFilter && "group-hover:opacity-20"
        )}
      >
        {columnDef.splitCellValue ? (
          <span className="text-sm">
            {value?.split(columnDef.splitCellValue)[FIRST_INDEX]}
            <br />
            {value?.split(columnDef.splitCellValue)[SECOND_INDEX]}
          </span>
        ) : columnDef.showThousandSeperator && !columnDef.cell ? (
          <span>{FormatNegativeWithParentheses(value)}</span>
        ) : (
          flexRender(columnDef.cell, context)
        )}{" "}
        <LogOut
          size={18}
          className={clsx(
            `mx-1 opacity-0 hidden group-hover:text-tableTextColorHover transition-all duration-200`,
            passedActionCheck && "group-hover:opacity-100  group-hover:block"
          )}
          title={columnDef.cellActionHelper}
        />
      </div>
    </div>
  );
};

export default renderCell;
